import { Form, Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import React from 'react'
import { formatError } from 'utils'

import { ErrorIndicator, Stepper } from 'components'
import { Box, Flex, Text } from '@chakra-ui/react'
import PropertyInfo from './PropertyInfo'
import Rooms from './Rooms'

import { useAppContext } from 'context/AppProvider'
import AdditionalInfo from './AdditionalInfo'
import PropertyImages from './PropertyImages'
import Location from './Location'
import Summary from './Summary'
import { PrimaryButton, SecondaryButton } from 'components/Buttons'
import {
  Agent,
  Enum_Property_Publish_State,
  Maybe,
  Property,
  UploadFile,
  useAddAgentPropertyMutation,
  useUpdateAgentPropertyMutation
} from 'generated/graphql'
import moment from 'moment'
import strapiHelpers from 'utils/strapiHelpers'
import { AxiosResponse } from 'axios'
import PropertyAgentManager from './PropertyAgentManager'
import { useAuthContext } from 'context/AuthProvider'

type RouteType = {
  label: string
  component: any
  key: string
}
export type AddPropertyValues = {
  Occupation_date: Date
  Lease_period: string
  Street_address: string
  Floor_size: number
  Bathrooms: number
  Bedrooms: number
  Kitchens: number
  Lounges: number
  Dining_rooms: number
  Parking: number
  Price: string
  Furnished: boolean
  Pool: boolean
  Description: string
  Publish_state: boolean
  Type_of_property: string
  Property_category: string
  Property_availability: string
  Rental_term: string
  Province: string
  District: string
  City: string
  Suburb: string
  images?: any[]
  agent: Agent | null
}

const AddPropertyForm: React.FC<{
  onSubmit: () => void
  property?: Maybe<Property>
  modalRef?: React.MutableRefObject<HTMLElement>
}> = ({ onSubmit, property }) => {
  const [propertyCreationStatus, setPropertyCreationStatus] = React.useState<
    'images' | 'property' | 'success' | 'error' | null
  >(null)

  const initialValues: AddPropertyValues = {
    Occupation_date: property?.occupation_date || new Date(),
    Lease_period: property?.lease_period || '',
    Street_address: property?.street_address || '',
    Floor_size: property?.floor_size || 10,
    Bathrooms: property?.bathrooms || 1,
    Bedrooms: property?.bedrooms || 1,
    Kitchens: property?.kitchens || 1,
    Lounges: property?.lounges || 1,
    Dining_rooms: property?.dining_rooms || 1,
    Parking: property?.parking || 1,
    Price: property?.price?.toString() || '0',
    Furnished: property?.furnished || false,
    Pool: property?.pool || false,
    Description: property?.description || '',
    Publish_state:
      (Enum_Property_Publish_State.Active === property?.publish_state ? true : false) || false,
    Type_of_property: property?.type_of_property?.id || '',
    Property_category: property?.property_category?.id || '',
    Property_availability: property?.property_availability?.id || '',
    images: [],
    Rental_term: property?.rental_term?.id || '',
    City: property?.suburb?.city?.id || '',
    Province: property?.suburb?.city?.province?.id || '',
    District: property?.suburb?.city?.district?.id || '',
    Suburb: property?.suburb?.id || '',
    agent: property?.agent || null
  }

  const [activeStep, setActiveStep] = React.useState(0)
  const validateAddPropertySchema = Yup.object().shape({
    ...(activeStep === 0 && {
      Price: Yup.string().required('Price is required'),
      Type_of_property: Yup.string().required('Type of property is required'),
      Property_category: Yup.string().required('Property category is required'),
      Property_availability: Yup.string().required('Property availability is required'),
      Rental_term: Yup.string().required('Rental term is required'),
      Lease_period: Yup.string().required('Lease period is required')
    }),
    ...(activeStep === 1 && {
      Street_address: Yup.string().required('Street address is required'),
      Province: Yup.string().required('Province is required'),
      District: Yup.string().required('District is required'),
      City: Yup.string().required('City is required'),
      Suburb: Yup.string().required('Suburb is required')
    }),
    ...(activeStep === 2 && {
      Floor_size: Yup.number().required('Floor size is required'),
      Bathrooms: Yup.number().required('Bathrooms is required'),
      Bedrooms: Yup.number().required('Bedrooms is required'),
      Kitchens: Yup.number().required('Kitchens is required'),
      Lounges: Yup.number().required('Lounges is required'),
      Dining_rooms: Yup.number().required('Dining rooms is required'),
      Parking: Yup.number().required('Parking is required')
    }),
    ...(activeStep === 3 && {
      Occupation_date: Yup.date().required('Occupation date is required'),
      Furnished: Yup.boolean().required('Furnished is required'),
      Pool: Yup.boolean().required('Pool is required')
    }),
    Description: Yup.string(),
    Publish_state: Yup.boolean()
  })
  const { isTabletOrMobile } = useAppContext()
  const { isAdmin } = useAuthContext()
  const [addProperty] = useAddAgentPropertyMutation({
    onCompleted: () => {
      onSubmit()
    }
  })
  const [updateProperty] = useUpdateAgentPropertyMutation({
    onCompleted: () => {
      onSubmit()
    }
  })
  const [existingImages, setExistingImages] = React.useState<
    Maybe<Maybe<UploadFile>[]> | undefined
  >(property?.photos)

  const stepInfo = [
    'Property Info',
    'Location',
    'Rooms',
    'Additional Info',
    'Images',
    ...(isAdmin ? ['Agent'] : []),
    'Summary'
  ]
  const routes: RouteType[] = [
    {
      label: 'Add Property Info',
      component: <PropertyInfo isTabletOrMobile={isTabletOrMobile} />,
      key: 'Add Property Info'
    },
    {
      label: 'Location',
      component: <Location isTabletOrMobile={isTabletOrMobile} />,
      key: 'Add Property Info'
    },
    {
      label: 'Rooms',
      component: <Rooms isTabletOrMobile={isTabletOrMobile} />,
      key: 'Rooms'
    },

    {
      label: 'Additional Info',
      component: <AdditionalInfo isTabletOrMobile={isTabletOrMobile} />,
      key: 'Additional Info'
    },
    {
      label: 'Add Property Images',
      component: <PropertyImages photos={existingImages} setPhotos={setExistingImages} />,
      key: 'Add Property Images'
    },
    ...(isAdmin
      ? [
          {
            label: 'Agent',
            component: <PropertyAgentManager />,
            key: 'Agent'
          }
        ]
      : []),
    {
      label: 'Summary',
      component: <Summary photos={existingImages} isTabletOrMobile={isTabletOrMobile} />,
      key: 'Additional Info'
    }
  ]

  return (
    <Formik
      validationSchema={validateAddPropertySchema}
      initialValues={initialValues}
      validateOnMount
      onSubmit={async (
        {
          Occupation_date,
          Lease_period,
          Street_address,
          Floor_size,
          Bathrooms,
          Bedrooms,
          Kitchens,
          Lounges,
          Dining_rooms,
          Parking,
          Price,
          Furnished,
          Pool,
          Description,
          Publish_state,
          Type_of_property,
          Property_category,
          Property_availability,
          Rental_term,
          Suburb,
          images,
          agent
        },
        { setStatus, setSubmitting }
      ) => {
        setStatus(null)

        setSubmitting(true)
        try {
          setPropertyCreationStatus('images')
          const imagesArray: AxiosResponse<UploadFile[]>[] = []

          for (const image of images || []) {
            const strapiFile = await strapiHelpers.upload(image?.file)
            imagesArray.push(strapiFile)
          }

          setPropertyCreationStatus('property')

          const data = {
            occupation_date: moment(Occupation_date).format('YYYY-MM-DD'),
            lease_period: Lease_period,
            street_address: Street_address,
            floor_size: Floor_size,
            bathrooms: Bathrooms,
            bedrooms: Bedrooms,
            photos:
              [
                ...(existingImages?.map((image) => image?.id || '') || []),
                ...imagesArray?.map((image) => image?.data?.[0]?.id)
              ] || [],
            kitchens: Kitchens,
            lounges: Lounges,
            dining_rooms: Dining_rooms,
            parking: Parking,
            price: parseInt(Price),
            furnished: Furnished,
            pool: Pool,
            description: Description,
            publish_state:
              Publish_state === true
                ? Enum_Property_Publish_State.Active
                : Enum_Property_Publish_State.Inactive,
            type_of_property: Type_of_property,
            property_category: Property_category,
            property_availability: Property_availability,
            rental_term: Rental_term,
            suburb: Suburb,
            agent: agent?.id || ''
          }
          if (property) {
            await updateProperty({
              variables: {
                input: {
                  id: property?.id || '',
                  input: {
                    data
                  }
                }
              }
            })
          } else {
            await addProperty({
              variables: {
                input: {
                  data
                }
              }
            })
          }

          setPropertyCreationStatus('success')
          setSubmitting(false)
        } catch (error: any) {
          setPropertyCreationStatus('error')
          console.log(error)
          setStatus(formatError(error))
        }
      }}
    >
      {({
        status,
        isSubmitting,
        handleSubmit,
        isValid,
        validateForm,
        values
      }: FormikProps<AddPropertyValues>) => (
        <Form style={{ width: '100%', margin: '1rem 0' }} autoComplete="off">
          <Stepper stepInfo={stepInfo} p={0} mt={6} mb={12} activeStep={activeStep}>
            {routes.map((route) => (
              <Box key={route.key}>
                {route.component}
                {status && <ErrorIndicator status={status} />}
                <Flex
                  direction={isTabletOrMobile ? 'column-reverse' : 'row'}
                  align={isTabletOrMobile ? 'flex-start' : 'center'}
                  mt={8}
                >
                  {!(activeStep === 0) && (
                    <SecondaryButton
                      onClick={async () => {
                        await validateForm(values)
                        setActiveStep((prev) => prev - 1)
                      }}
                      mr={8}
                      isLoading={isSubmitting}
                      minW={isTabletOrMobile ? '100%' : undefined}
                      disabled={activeStep === 0}
                    >
                      {activeStep === 0 ? 'Cancel' : 'Back'}
                    </SecondaryButton>
                  )}
                  <PrimaryButton
                    my={8}
                    mr={4}
                    minW={isTabletOrMobile ? '100%' : 52}
                    isDisabled={isSubmitting || !isValid}
                    onClick={
                      activeStep === routes.length - 1
                        ? () => handleSubmit()
                        : async () => {
                            if (isValid) {
                              setActiveStep((prev) => prev + 1)
                            } else {
                              validateForm()
                            }
                          }
                    }
                    type={activeStep === routes.length - 1 ? 'submit' : 'button'}
                    isLoading={isSubmitting}
                  >
                    {activeStep === routes.length - 1 ? 'Add Property' : 'Next'}
                  </PrimaryButton>
                  {propertyCreationStatus && (
                    <Text fontSize="x-small" color="gray.500">
                      Creating {propertyCreationStatus} ...
                    </Text>
                  )}
                </Flex>
              </Box>
            ))}
          </Stepper>
        </Form>
      )}
    </Formik>
  )
}

export default AddPropertyForm
